import { put, select, take } from 'redux-saga/effects';
import * as actions from '../actions';
import { setHasPostings, setHasPublishedPostings } from '../actions/PermanentJobs/PermanentJobs';
import history from '../browserHistory';
import { apiMethodsConst } from '../services/methods';
import { POSTHOG_EVENTS, trackPosthogEvent } from '../services/posthog';
import { store } from '../store/configureStore';
import { callAndDispatch } from './utils';

export function* watchEditUserAccountInfo() {
  const events = actions.createRequestTypes(actions.EDIT_ACCOUNT_INFO_ACTION);
  while (true) {
    const data = yield take(actions.EDIT_ACCOUNT_INFO_ACTION);

    yield put({ type: events.REQUEST });

    try {
      yield* callAndDispatch(apiMethodsConst.EDIT_USER_REQUEST, data);

      if (data.place_id) {
        yield* callAndDispatch(apiMethodsConst.ADD_ADDRESS_REQUEST, data.place_id);
      }

      if (data.productivity) {
        yield* callAndDispatch(apiMethodsConst.ADD_OFFICE_PRODUCTIVITY_REQUEST, data.productivity);
      }

      trackPosthogEvent(POSTHOG_EVENTS.DENTAL_PORTAL_OFFICE_SIGNUP_GENERAL_OFFICE_INFO_COMPLETED);

      if (data.avatar) {
        const formData = new FormData();
        formData.append('image', data.avatar);

        yield* callAndDispatch(apiMethodsConst.UPLOAD_AVATAR_REQUEST, formData);
      }

      const currentUser = yield select((state) => state.user.user);
      const isPhoneAlreadyOnFile = currentUser?.contact_number === data.contact_number;

      if (data.contact_number && !isPhoneAlreadyOnFile) {
        yield* callAndDispatch(apiMethodsConst.SEND_SMS_CODE_REQUEST, data.contact_number);
      } else {
        history.push('/registration/office-info');
      }

      yield put({ type: events.SUCCESS });
    } catch (error) {
      trackPosthogEvent(POSTHOG_EVENTS.DENTAL_PORTAL_OFFICE_SIGNUP_GENERAL_OFFICE_INFO_FAILED);

      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

export function* watchEditUserGeneralInfo() {
  const events = actions.createRequestTypes(actions.EDIT_GENERAL_INFO_ACTION);
  while (true) {
    const data = yield take(actions.EDIT_GENERAL_INFO_ACTION);
    yield put({ type: events.REQUEST });
    try {
      // edit
      yield* callAndDispatch(apiMethodsConst.EDIT_USER_REQUEST, data);

      history.push('/registration/admin');

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

export function* watchEditUserOfficeInfo() {
  const events = actions.createRequestTypes(actions.EDIT_USER_OFFICE_INFO);

  while (true) {
    const { data, insurances, jobId } = yield take(actions.EDIT_USER_OFFICE_INFO);
    yield put({ type: events.REQUEST });

    try {
      // edit
      yield* callAndDispatch(apiMethodsConst.EDIT_USER_REQUEST, { office_info: data });

      if (data?.education) {
        yield* callAndDispatch(
          apiMethodsConst.AUTH_REFRESH_REQUEST,
          localStorage.getItem('refresh_token_dental'),
        );

        yield* callAndDispatch(apiMethodsConst.UPDATE_USER_EDUCATION_REQUEST, {
          education: data.education,
          insurances: insurances || [],
        });

        yield* callAndDispatch(apiMethodsConst.USER_CREDENTIALS_REQUEST);
      }

      if (jobId) {
        history.push(`/dashboard/job/${jobId}`, {
          jobId,
        });
      }
      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

export function* watchEditUser() {
  const events = actions.createRequestTypes(actions.EDIT_USER);

  while (true) {
    const { data, education, insurances, location } = yield take(actions.EDIT_USER);

    yield put({ type: events.REQUEST });

    try {
      // edit
      const user = store.getState().user?.user;
      const address = store.getState().user?.user?.address?.[0];

      if (data.place_id && address?.place_id !== data.place_id) {
        if (address) {
          yield* callAndDispatch(apiMethodsConst.DELETE_ADDRESSES_REQUEST);
        }
        yield* callAndDispatch(apiMethodsConst.ADD_ADDRESS_REQUEST, data.place_id);
      }

      if (data.productivity) {
        yield* callAndDispatch(apiMethodsConst.ADD_OFFICE_PRODUCTIVITY_REQUEST, data.productivity);
      }

      if (data.is_profile_complete) {
        localStorage.setItem(`isNewToYouFeatureNew-${user?.id}`, 'false');
        localStorage.setItem(`isExpirationUpdatePopupNew-${user?.id}`, 'false');
        localStorage.setItem('tempmeeBecomingGoTuPopup', false);
        localStorage.setItem(`showNewAutoConfirmOptionPopup-${user?.id}`, 'false');
        localStorage.setItem(`selectWorkerTypePopup-${user?.id}`, 'false');
        localStorage.setItem(`candidateSortAnnouncement-${user.id}`, 'false');
      }

      if (education) {
        yield* callAndDispatch(
          apiMethodsConst.AUTH_REFRESH_REQUEST,
          localStorage.getItem('refresh_token_dental'),
        );

        yield* callAndDispatch(apiMethodsConst.UPDATE_USER_EDUCATION_REQUEST, {
          education,
          insurances,
        });
      }

      yield* callAndDispatch(apiMethodsConst.EDIT_USER_REQUEST, data);

      if (data.is_profile_complete) {
        yield* callAndDispatch(
          apiMethodsConst.AUTH_REFRESH_REQUEST,
          localStorage.getItem('refresh_token_dental'),
        );
        yield put(setHasPostings(false));
        yield put(setHasPublishedPostings(false));
      }

      // getting current user credentials and store it
      yield* callAndDispatch(apiMethodsConst.USER_CREDENTIALS_REQUEST);

      if (location) {
        history.push(location);
      }

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

export function* watchUploadAvatar() {
  const events = actions.createRequestTypes(actions.UPLOAD_AVATAR_ACTION);
  while (true) {
    const { avatar } = yield take(actions.UPLOAD_AVATAR_ACTION);
    yield put({ type: events.REQUEST });
    try {
      const formData = new FormData();
      formData.append('image', avatar);
      yield* callAndDispatch(apiMethodsConst.UPLOAD_AVATAR_REQUEST, formData);

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response.data });
    }
  }
}

export function* watchPhoneVerification() {
  const events = actions.createRequestTypes(actions.PHONE_VERIFICATION_ACTION);
  while (true) {
    const data = yield take(actions.PHONE_VERIFICATION_ACTION);
    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.PHONE_VERIFICATION_REQUEST, {
        code: data.code,
        phone: data.phone,
      });
      yield* callAndDispatch(apiMethodsConst.AUTH_ME_REQUEST);
      // yield* callAndDispatch(apiMethodsConst.FETCH_ALL_PP_JOBS);
      yield* callAndDispatch(apiMethodsConst.FETCH_AVERAGE_RATING);

      trackPosthogEvent(POSTHOG_EVENTS.DENTAL_PORTAL_MFA_CODE_COMPLETED);

      history.push('/registration/office-info');
      yield put({ type: events.SUCCESS });
    } catch (error) {
      trackPosthogEvent(POSTHOG_EVENTS.DENTAL_PORTAL_MFA_CODE_FAILED);
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

export function* watchCloseModalWithCode() {
  const events = actions.createRequestTypes(actions.CLOSE_MODAL_ACTION);
  while (true) {
    yield take(actions.CLOSE_MODAL_ACTION);
    yield put({ type: events.REQUEST });
    try {
      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

